import { BoldLabelText } from '@/components/BoldLabelText/BoldLabelText';
import { DescriptionCol, DescriptionRow } from '@/components/Description';
import {
  TransactionItemDrawerMode,
  TransactionItemDrawerModeEnum,
  TransactionItemFormType,
} from '@/features/transaction_items/List/type';
import { useTranslation } from '@/i18n';
import {
  Caption,
  FormInput,
  Label,
} from '@moneyforward/ap-frontend-components';
import classNames from 'classnames';
import { FC, memo, useMemo } from 'react';
import { Control, FieldErrors } from 'react-hook-form';
import styles from './TooltipInput.module.scss';

const cx = classNames.bind(styles);

export type Props = {
  mode: TransactionItemDrawerMode;
  control: Control<TransactionItemFormType>;
  value?: string;
  errors: FieldErrors<TransactionItemFormType>['tooltip_message'];
};

const MAX_LENGTH = 100;

export const TooltipInput: FC<Props> = memo(
  ({ control, errors, mode, value }) => {
    const { t } = useTranslation();

    const hasError = !!Object.values(errors || {}).length;

    const rules = useMemo(() => {
      return {
        require: false,
        maxLength: {
          value: MAX_LENGTH,
          message: t('transaction_items.validation_message.maxLength', {
            maxLength: MAX_LENGTH,
          }),
        },
      };
    }, [t]);

    return (
      <DescriptionRow>
        <DescriptionCol
          label={
            <Label testId='label'>
              <BoldLabelText>
                {t('transaction_items.form.tooltip_message')}
                <Caption type='helper' icon={false}>
                  {t('transaction_items.form.tooltip_message.note')}
                </Caption>
              </BoldLabelText>
            </Label>
          }
          labelWidth='176px'
        />
        <DescriptionCol
          value={
            <>
              {mode === TransactionItemDrawerModeEnum.DETAIL ? (
                <div data-testid='label-detail'>{value ?? ''}</div>
              ) : (
                <div className={cx(styles['input-wrapper'])}>
                  <FormInput<TransactionItemFormType>
                    name='tooltip_message'
                    control={control}
                    inputId='tooltip_message'
                    testId='tooltip_message'
                    rules={rules}
                  />
                  {!hasError && (
                    <Caption type='helper'>
                      {t('transaction_items.form.tooltip_message.caption', {
                        maxLength: MAX_LENGTH,
                      })}
                    </Caption>
                  )}
                </div>
              )}
            </>
          }
        />
      </DescriptionRow>
    );
  }
);
TooltipInput.displayName = 'TooltipInput';
